import { useState, useEffect } from 'react';
import { intelligenceAPI, SupplierQualification, QualificationTemplate, ConnectionCompany, coreAPI } from '../../lib/api';
import { toast } from 'sonner';

export interface UseQualificationsDataResult {
  qualifications: SupplierQualification[];
  loading: boolean;
  templates: QualificationTemplate[];
  suppliers: ConnectionCompany[];
  /** True while the assign dialog's supplier list is being fetched. */
  suppliersLoading: boolean;
  loadQualifications: () => Promise<void>;
}

/**
 * Loads and manages the data driving the Supplier Qualifications page:
 * the qualifications list, the questionnaire templates, and the suppliers
 * used by the assignment dialog. Behaviour matches the original inline
 * loaders and effects exactly.
 *
 * @param filterStatus - Current status filter ('all' or a status key)
 * @param isCustomer - Whether the active portal mode is customer
 * @param showAssignDialog - Whether the assign dialog is open (drives supplier load)
 * @returns The loaded data plus a `loadQualifications` reloader
 */
export function useQualificationsData(
  filterStatus: string,
  isCustomer: boolean,
  showAssignDialog: boolean,
): UseQualificationsDataResult {
  const [qualifications, setQualifications] = useState<SupplierQualification[]>([]);
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState<QualificationTemplate[]>([]);
  const [suppliers, setSuppliers] = useState<ConnectionCompany[]>([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);

  const loadQualifications = async () => {
    try {
      setLoading(true);
      // Fetch ALL qualifications by paginating through all pages
      const allQualifications: SupplierQualification[] = [];
      let page = 1;
      const pageSize = 100;

      while (true) {
        const params: Record<string, string | number | boolean> = { page_size: pageSize, page };
        if (filterStatus !== 'all') params['status'] = filterStatus;

        const response = await intelligenceAPI.getQualifications(params);
        const results = response.data.results || [];
        allQualifications.push(...results);

        // Check if we've fetched all items
        if (allQualifications.length >= response.data.count || results.length < pageSize) {
          break;
        }
        page++;
      }

      setQualifications(allQualifications);
    } catch (err: unknown) {
      console.error('Error loading qualifications:', err);
      toast.error('Failed to load questionnaires');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      // Fetch ALL templates
      const allTemplates: QualificationTemplate[] = [];
      let page = 1;
      const pageSize = 100;

      while (true) {
        const response = await intelligenceAPI.getTemplates({ page_size: pageSize, page });
        const results = response.data.results || [];
        allTemplates.push(...results);

        if (allTemplates.length >= response.data.count || results.length < pageSize) {
          break;
        }
        page++;
      }

      setTemplates(allTemplates);
    } catch (err: unknown) {
      console.error('Error loading templates:', err);
    }
  };

  const loadSuppliers = async () => {
    try {
      setSuppliersLoading(true);
      // The generic /core/companies/ list is IsHelixUser-only, so a customer
      // admin opening the Assign dialog got a 403 and an empty supplier
      // dropdown — the one role the dialog exists for could not use it.
      // my_connections is open to any authenticated user and scoped
      // server-side to the caller's approved suppliers (or, for a helix
      // user, every approved supplier). include_onboarding adds suppliers
      // still being onboarded (draft, due diligence…) — the ones onboarding
      // questionnaires are sent to — but never rejected ones. Server cap is
      // 2000; the dialog's picker is searchable.
      const response = await coreAPI.getMyConnections({ limit: 2000, include_onboarding: true });
      setSuppliers(response.data.filter((c) => c.as_supplier));
    } catch (err: unknown) {
      console.error('Error loading suppliers:', err);
      // An empty picker otherwise reads as "no suppliers on file".
      toast.error('Failed to load suppliers');
    } finally {
      setSuppliersLoading(false);
    }
  };

  useEffect(() => {
    loadQualifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, isCustomer]);

  useEffect(() => {
    // Load templates for matrix view column headers
    loadTemplates();
    if (showAssignDialog && isCustomer) {
      loadSuppliers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAssignDialog]);

  return { qualifications, loading, templates, suppliers, suppliersLoading, loadQualifications };
}
